import type { AttributeValue } from "@opentelemetry/api"
import type { ReadableSpan, SpanProcessor } from "@opentelemetry/sdk-trace-web"

/**
 * URL-bearing span attributes that may carry PII (query string / path ids),
 * redacted in place. The `entry_page.*` entries are defensive only — those are
 * resource attributes a span processor can't reach; `entryPageAttributes` in
 * initTelemetry is the real guarantee.
 */
const URL_ATTRIBUTE_KEYS = [
  "url.full",
  "http.url",
  "http.target", // older semconv: path + query
  "page.url",
  "url.path",
  "page.route",
  "entry_page.url",
] as const

/** Raw search-string attributes deleted outright — pure PII. */
const SEARCH_ATTRIBUTE_KEYS = [
  "page.search",
  "entry_page.search",
  "url.query", // older semconv: bare query string
] as const

const UUID_PATTERN =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g
const EMAIL_PATTERN = /[^/\s?&=@]+@[^/\s?&=@]+\.[^/\s?&=@]+/g
// Numeric segment before `/`, `#` (hash route), or end — so `/orders/12345#/x` redacts too.
const NUMERIC_SEGMENT_PATTERN = /\/\d+(?=[/#]|$)/g

function removeQuery(part: string): string {
  const queryIndex = part.indexOf("?")
  return queryIndex === -1 ? part : part.slice(0, queryIndex)
}

/**
 * Drop the query string, keeping a hash-router route. The query can sit before
 * the fragment (`…?q#/route`) or inside it (`…#/route?q`), so strip from both.
 */
export function stripQueryString(url: string): string {
  const hashIndex = url.indexOf("#")
  if (hashIndex === -1) {
    return removeQuery(url)
  }
  const base = removeQuery(url.slice(0, hashIndex))
  const fragment = removeQuery(url.slice(hashIndex + 1))
  return `${base}#${fragment}`
}

/** Replace path-embedded ids (UUIDs, emails, numeric segments) with `:id`. */
export function templatizePath(value: string): string {
  return value
    .replace(UUID_PATTERN, ":id")
    .replace(EMAIL_PATTERN, ":id")
    .replace(NUMERIC_SEGMENT_PATTERN, "/:id")
}

/** Strip the query string then templatize path ids. */
export function redactUrlValue(value: string): string {
  return templatizePath(stripQueryString(value))
}

/**
 * Redact URL-bearing attributes and delete raw search attributes, mutating the
 * given attribute bag in place.
 */
export function redactAttributes(
  attributes: Record<string, AttributeValue>
): void {
  for (const key of URL_ATTRIBUTE_KEYS) {
    const value = attributes[key]
    if (typeof value === "string") {
      attributes[key] = redactUrlValue(value)
    }
  }
  for (const key of SEARCH_ATTRIBUTE_KEYS) {
    delete attributes[key]
  }
}

/**
 * Scrubs URL query strings and templatizes path ids on every span before
 * export. Must run before the exporting processor so the mutation is seen.
 */
export class RedactingSpanProcessor implements SpanProcessor {
  onStart(): void {}

  onEnd(span: ReadableSpan): void {
    const attributes = span.attributes as Record<string, AttributeValue>
    try {
      redactAttributes(attributes)
    } catch {
      // Fail closed: if redaction ever throws, drop the URL-bearing attributes
      // rather than risk exporting an unredacted value on the hot path.
      for (const key of [...URL_ATTRIBUTE_KEYS, ...SEARCH_ATTRIBUTE_KEYS]) {
        delete attributes[key]
      }
    }
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}
